import { useState } from 'react';
import { Loader2, X, ShieldCheck } from 'lucide-react';
import { usersApi } from '@/services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onApproved: (approverId: string) => void;
  title?: string;
  message?: string;
}

export default function ManagerOverrideModal({
  open,
  onClose,
  onApproved,
  title = 'Manager Approval Required',
  message = 'A manager must approve this action.',
}: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter manager username and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await usersApi.verifyManagerOverride({ username: username.trim(), password });
      const approver = res.data.message?.user;
      if (!approver) throw new Error('Invalid credentials');
      onApproved(approver);
      setUsername('');
      setPassword('');
      onClose();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'Manager verification failed',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold">{title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <p className="text-sm text-gray-600">{message}</p>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input w-full"
            placeholder="Manager username"
            autoComplete="username"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input w-full"
            placeholder="Password"
            autoComplete="current-password"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Approve
          </button>
        </form>
      </div>
    </div>
  );
}
